import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CanvasJSReact from "@canvasjs/react-charts";

import Logo from "../images/logo1.png";
import styles from "../css/landing.module.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPeopleGroup,
  faUserPlus,
  faUserGroup,
  faClipboardUser,
  faMoneyBill,
  faMoneyBill1,
  faReceipt,
  faUser,
  faHouse,
  faArrowRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function Faithhome() {

  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);

  // =============================
  // FETCH MEMBERS
  // =============================

  useEffect(() => {

    axios
      .get("https://appserver.cemkharistemple.com/faithallmembers")
      .then((res) => setUsers(res.data))
      .catch((err) => console.error(err));

  }, []);

  const totalMembers = users.length;

  // =============================
  // FETCH ATTENDANCE
  // =============================

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = async () => {

    try {

      const res = await fetch(
        "https://appserver.cemkharistemple.com/faithattendance"
      );

      const result = await res.json();

      // ===== ORIGINAL ATTENDANCE LINE CHART =====

      const formatted = result.map((item) => ({
        label: item.service_date,
        y: parseFloat(item.total_count),
      }));

      setAttendanceData(formatted);

      processAttendanceCharts(result);

    } catch (err) {

      console.error(err);

    }

  };

  // =============================
  // PROCESS ATTENDANCE TABLE
  // =============================

  const processAttendanceCharts = (data) => {

    const yearly = {};
    const monthly = {};

    data.forEach((item) => {

      const date = new Date(item.service_date);

      const year = date.getFullYear();

      const month = date.toLocaleString("default", { month: "short" });

      if (!yearly[year]) yearly[year] = { male: 0, female: 0 };

      if (!monthly[`${year}-${month}`])
        monthly[`${year}-${month}`] = { male: 0, female: 0 };

      yearly[year].male += Number(item.male || 0);
      yearly[year].female += Number(item.female || 0);

      monthly[`${year}-${month}`].male += Number(item.male || 0);
      monthly[`${year}-${month}`].female += Number(item.female || 0);

    });

    const sortedYears = Object.keys(yearly).sort();

    setYearlyData([
      {
        type: "line",
        showInLegend: true,
        legendText: "Male",
        dataPoints: sortedYears.map((y) => ({
          label: y,
          y: yearly[y].male,
        })),
      },
      {
        type: "line",
        showInLegend: true,
        legendText: "Female",
        dataPoints: sortedYears.map((y) => ({
          label: y,
          y: yearly[y].female,
        })),
      },
    ]);

    const sortedMonths = Object.keys(monthly).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    setMonthlyData([
      {
        type: "column",
        showInLegend: true,
        legendText: "Male",
        dataPoints: sortedMonths.map((m) => ({
          label: m,
          y: monthly[m].male,
        })),
      },
      {
        type: "column",
        showInLegend: true,
        legendText: "Female",
        dataPoints: sortedMonths.map((m) => ({
          label: m,
          y: monthly[m].female,
        })),
      },
    ]);

  };

  // =============================
  // CHART OPTIONS
  // =============================

  const attendanceOptions = {
    animationEnabled: true,
    title: { text: "Attendance Overview" },
    axisY: { title: "Total Count" },
    axisX: { labelAngle: -45 },
    data: [
      {
        type: "line",
        dataPoints: attendanceData,
      },
    ],
  };

  const yearlyOptions = {
    animationEnabled: true,
    title: { text: "Male vs Female (Yearly Trend)" },
    axisY: { title: "Total Attendance" },
    data: yearlyData,
  };

  const monthlyOptions = {
    animationEnabled: true,
    title: { text: "Male vs Female (Monthly Bar)" },
    axisY: { title: "Total Attendance" },
    axisX: { labelAngle: -45 },
    data: monthlyData,
  };

  const handleLogout = () => {

    localStorage.removeItem("accessToken");
    localStorage.removeItem("token");

    window.location.href = "/";

  };

  return (

    <div className={styles.container}>

      {/* HEADER */}

      <header className={styles.header}>

        <div className={styles.headerCenter}>
          <div className={styles.pageTitleWrap}>
            <h1 className={styles.pageTitle}>Faith Temple</h1>
            <p className={styles.pageSubtitle}>
              Members, attendance & finance overview
            </p>
          </div>
        </div>

        <div className={styles.headerRight}>

          <div className={styles.userChip}>
            <span className={styles.userIcon}>
              <FontAwesomeIcon icon={faUser} />
            </span>
            <span className={styles.userText}>Admin</span>
          </div>

          <button
            className={styles.iconBtn}
            onClick={() => navigate("/landing")}
          >
            <FontAwesomeIcon icon={faHouse} />
          </button>

          <button
            className={`${styles.iconBtn} ${styles.dangerBtn}`}
            onClick={handleLogout}
          >
            <FontAwesomeIcon icon={faArrowRightFromBracket} />
          </button>

        </div>

      </header>

      {/* SIDEBAR */}

      <aside className={styles.sidebar}>

        <div className={styles.sideTop}>

          <div className={styles.logoWrap}>
            <img src={Logo} alt="Faith Temple" className={styles.logoImg} />
          </div>

          <div className={styles.brandText}>
            <div className={styles.brandName}>
              Faith <span className={styles.brandAccent}>Admin</span>
            </div>
            <div className={styles.brandSmall}>Temple Dashboard</div>
          </div>

        </div>

        <nav className={styles.nav}>

          <button
            className={styles.navItem}
            onClick={() => navigate("/addmemberfaith")}
          >
            <FontAwesomeIcon icon={faUserPlus} /> Add Member
          </button>

          <button
            className={styles.navItem}
            onClick={() => navigate("/allmembersfaith")}
          >
            <FontAwesomeIcon icon={faUserGroup} /> View Members
          </button>

          <button
            className={styles.navItem}
            onClick={() => navigate("/attendancefaith")}
          >
            <FontAwesomeIcon icon={faClipboardUser} /> Attendance
          </button>

          <button
            className={styles.navItem}
            onClick={() => navigate("/paytithefaith")}
          >
            <FontAwesomeIcon icon={faMoneyBill} /> Tithe
          </button>

          <button
            className={styles.navItem}
            onClick={() => navigate("/payfirstfruitfaith")}
          >
            <FontAwesomeIcon icon={faMoneyBill1} /> First Fruit
          </button>

          <button className={styles.navItem}>
            <FontAwesomeIcon icon={faReceipt} /> Finance
          </button>

        </nav>

      </aside>

      {/* MAIN */}

      <main className={styles.main}>

        <section className={styles.stats}>

          <div className={styles.statCard}>

            <div className={styles.statTop}>Total Members</div>

            <div className={styles.statValue}>
              {totalMembers}
            </div>

            <div className={styles.statHint}>
              Active members
            </div>

          </div>

        </section>

        <section className={styles.section}>

          <div className={styles.sectionHeader}>

            <h2 className={styles.sectionTitle}>
              Attendance Trend
            </h2>

            <p className={styles.sectionSub}>
              Weekly service attendance performance
            </p>

          </div>

          <div className={styles.statCard}>
            <CanvasJSChart options={attendanceOptions} />
          </div>

        </section>

        <section className={styles.section}>

          <div className={styles.statCard}>
            <CanvasJSChart options={yearlyOptions} />
          </div>

        </section>

        <section className={styles.section}>

          <div className={styles.statCard}>
            <CanvasJSChart options={monthlyOptions} />
          </div>

        </section>

      </main>

    </div>

  );
}

export default Faithhome;